<template>
  <!-- Auth gate -->
  <div v-if="!isAdmin" class="login-container">
    <el-card class="login-card">
      <h2>Admin access required</h2>
      <p style="text-align:center;color:#555;margin-bottom:1.5rem">
        You need to be logged in as an admin to manage departments.
      </p>
      <router-link to="/manage-cards">
        <button type="button"><i class="fas fa-arrow-left"></i> Go to admin login</button>
      </router-link>
    </el-card>
  </div>

  <div v-else id="manage-departments-page" class="admin-page">
    <AdminTabs />

    <div class="admin-page-header">
      <h1>Manage Departments</h1>
      <button type="button" class="admin-btn admin-btn--primary" @click="openAddDepartmentModal">
        <i class="fas fa-plus"></i> Add Department
      </button>
    </div>

    <!-- Department list -->
    <div class="admin-table-wrap">
   
      <div v-if="!departments.length" class="admin-state">No departments yet.</div>

      <table v-else class="admin-table">
        <thead>
          <tr>
            <th style="width:2.5rem"></th>
            <th style="width:2.5rem">#</th>
            <th>Name</th>
            <th style="width:5rem">Cards</th>
            <th style="width:7rem">Colors</th>
            <th class="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody ref="departmentList">
          <tr
            v-for="(dept, index) in sortedDepartments"
            :key="dept.id"
            draggable="true"
            :class="{ 'dragging': draggedDepartment?.id === dept.id }"
            @dragstart="startDrag($event, dept, index)"
            @dragend="resetDragState"
            @dragover.prevent
            @drop="onDrop($event, index)"
            @dragenter.prevent
          >
            <td class="drag-handle"><i class="fas fa-grip-vertical"></i></td>
            <td>{{ index + 1 }}</td>
            <td class="dept-name-cell">
              <strong>{{ dept.name }}</strong>
              <span v-if="dept.description" class="dept-desc">{{ dept.description }}</span>
            </td>
            <td>{{ getCardCountForDepartment(dept.id) }}</td>
            <td>
              <div class="color-swatches" v-if="dept.primary_color || dept.secondary_color">
                <span
                  class="swatch"
                  :style="{ background: dept.primary_color || '#000' }"
                  :title="`Primary: ${dept.primary_color}`"
                ></span>
                <span
                  class="swatch"
                  :style="{ background: dept.secondary_color || '#fff' }"
                  :title="`Secondary: ${dept.secondary_color}`"
                ></span>
              </div>
              <span v-else class="text-muted">—</span>
            </td>
            <td class="col-actions">
              <div class="admin-icon-btn-group">
                <el-tooltip content="Edit department" placement="top" transition="tooltip-no-fade" :show-after="0" :hide-after="0" :enterable="false">
                  <button type="button" class="admin-icon-btn" @click.stop="editDepartment(dept)">
                    <i class="fas fa-pencil-alt"></i>
                  </button>
                </el-tooltip>
                <el-tooltip content="Delete department" placement="top" transition="tooltip-no-fade" :show-after="0" :hide-after="0" :enterable="false">
                  <button type="button" class="admin-icon-btn admin-icon-btn--delete" @click.stop="deleteDepartmentRow(dept)">
                    <i class="fas fa-trash"></i>
                  </button>
                </el-tooltip>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="admin-table-footer">
        <button type="button" class="admin-add-row-btn" @click="openAddDepartmentModal">
          <i class="fas fa-plus"></i> Add Department
        </button>
      </div>
    </div>

    <!-- Add / Edit Department modal -->
    <SettingsModal :show="showDepartmentModal" @update:show="showDepartmentModal = $event">
      <template #header>
        <h3>{{ isEditingDepartment ? 'Edit Department' : 'Add New Department' }}</h3>
      </template>

      <template #body>
        <form @submit.prevent="isEditingDepartment ? updateDepartment() : addDepartment()" class="department-form">
          <div class="form-section">
            <h4>Basic Information</h4>
            <div class="admin-form-group">
              <label class="admin-label" for="deptName">Department Name</label>
              <input type="text" id="deptName" class="admin-input" v-model="editingDepartment.name" required placeholder="Enter department name" />
            </div>
            <div class="admin-form-group">
              <label class="admin-label" for="deptDesc">Description</label>
              <input type="text" id="deptDesc" class="admin-input" v-model="editingDepartment.description" required placeholder="Department description" />
            </div>
            <div class="admin-form-group">
              <label class="admin-label" for="demoCode">Demo access code</label>
              <input type="text" id="demoCode" class="admin-input" v-model="editingDepartment.demoAccessCode" autocomplete="off" placeholder="Optional — leave empty if unused" />
              <p class="admin-hint" style="margin-top:0.4rem;margin-bottom:0">Stored uppercase. Must be unique. Leave empty if not used.</p>
            </div>
          </div>

          <div class="form-section">
            <h4>Card Images</h4>
            <div class="image-upload-grid">
              <div class="image-upload-group">
                <label class="admin-label" for="cardFrontImage">Card Front Image</label>
                <div class="image-upload-container">
                  <input type="file" id="cardFrontImage" @change="handleImageUpload('front', $event)" accept="image/*" class="image-input" />
                  <div class="image-preview" v-if="editingDepartment.frontImage">
                    <img :src="editingDepartment.frontImage" alt="Front preview" />
                    <button type="button" @click="removeImage('front')" class="remove-image"><i class="fas fa-times"></i></button>
                  </div>
                  <div class="upload-placeholder" v-else>
                    <i class="fas fa-image"></i>
                    <span>Click to upload front image</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <h4>Color Scheme</h4>
            <div class="color-selector-grid">
              <div class="color-selector-group">
                <label class="admin-label" for="primaryColor">Primary Color</label>
                <div class="color-input-group">
                  <input type="color" id="primaryColor" v-model="editingDepartment.primaryColor" class="color-picker" />
                  <input type="text" v-model="editingDepartment.primaryColor" placeholder="#000000" class="color-hex" pattern="^#[0-9A-Fa-f]{6}$" />
                </div>
              </div>
              <div class="color-selector-group">
                <label class="admin-label" for="secondaryColor">Secondary Color</label>
                <div class="color-input-group">
                  <input type="color" id="secondaryColor" v-model="editingDepartment.secondaryColor" class="color-picker" />
                  <input type="text" v-model="editingDepartment.secondaryColor" placeholder="#FFFFFF" class="color-hex" pattern="^#[0-9A-Fa-f]{6}$" />
                </div>
              </div>
            </div>
          </div>
        </form>
      </template>

      <template #footer>
        <button v-if="isEditingDepartment" type="button" class="modal-delete-link" @click="deleteDepartment">
          <i class="fas fa-trash"></i> Delete department
        </button>
        <button type="button" class="admin-btn admin-btn--primary" @click="isEditingDepartment ? updateDepartment() : addDepartment()">
          <i class="fas fa-save"></i> Save
        </button>
      </template>
    </SettingsModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import AdminTabs from '@/components/AdminTabs.vue'
import SettingsModal from '@/components/SettingsModal.vue'
import { supabase } from '@/supabase.js'
import { ElMessage } from 'element-plus'
import { useGameStore } from '@/stores/gameStore'

const gameStore = useGameStore()

// ── Auth ─────────────────────────────────────────────────────
const isAdmin = ref(false)
onMounted(async () => {
  if (localStorage.getItem('loggedIn') === 'true' && localStorage.getItem('adminUser')) {
    isAdmin.value = true
    await Promise.all([fetchDepartments(), gameStore.fetchAllCards()])
  }
})

// ── Departments data ──────────────────────────────────────────
const departments = ref([])
const fetchDepartments = async () => {
  const { data, error } = await supabase.from('departments').select('*')
  if (error) {
    console.error('Error fetching departments:', error)
    ElMessage.error('Could not load departments.')
  } else {
    departments.value = data
  }
}

const sortedDepartments = computed(() =>
  [...departments.value].sort((a, b) => (a.position || 0) - (b.position || 0))
)

const getCardCountForDepartment = (departmentId) =>
  gameStore.allCards.filter(card => card.department === departmentId).length

// ── Modal state ───────────────────────────────────────────────
const showDepartmentModal = ref(false)
const isEditingDepartment = ref(false)
const editingDepartment = ref({
  name: '', description: '', demoAccessCode: '',
  frontImage: '', primaryColor: '#000000', secondaryColor: '#FFFFFF'
})

const openAddDepartmentModal = () => {
  editingDepartment.value = {
    name: '', description: '', demoAccessCode: '',
    frontImage: '', primaryColor: '#000000', secondaryColor: '#FFFFFF'
  }
  isEditingDepartment.value = false
  showDepartmentModal.value = true
}

const editDepartment = (dept) => {
  editingDepartment.value = {
    id: dept.id,
    name: dept.name,
    description: dept.description || '',
    demoAccessCode: dept.demo_access_code || '',
    frontImage: dept.front_image || '',
    primaryColor: dept.primary_color || '#000000',
    secondaryColor: dept.secondary_color || '#FFFFFF',
  }
  isEditingDepartment.value = true
  showDepartmentModal.value = true
}

const closeDepartmentModal = () => {
  showDepartmentModal.value = false
  isEditingDepartment.value = false
  editingDepartment.value = {
    name: '', description: '', demoAccessCode: '',
    frontImage: '', primaryColor: '#000000', secondaryColor: '#FFFFFF'
  }
}

// ── CRUD ──────────────────────────────────────────────────────
const addDepartment = async () => {
  try {
    const maxPosition = Math.max(...departments.value.map(d => d.position || 0), -1)
    const demoCodeRaw = (editingDepartment.value.demoAccessCode || '').trim()
    const { error } = await supabase.from('departments').insert([{
      name: editingDepartment.value.name,
      description: editingDepartment.value.description,
      demo_access_code: demoCodeRaw ? demoCodeRaw.toUpperCase() : null,
      front_image: editingDepartment.value.frontImage,
      primary_color: editingDepartment.value.primaryColor,
      secondary_color: editingDepartment.value.secondaryColor,
      position: maxPosition + 1,
    }])
    if (error) throw error
    await fetchDepartments()
    closeDepartmentModal()
    ElMessage.success('Department added.')
  } catch (e) {
    console.error(e)
    ElMessage.error('Error adding department: ' + e.message)
  }
}

const updateDepartment = async () => {
  try {
    const demoCodeRaw = (editingDepartment.value.demoAccessCode || '').trim()
    const { error } = await supabase.from('departments').update({
      name: editingDepartment.value.name,
      description: editingDepartment.value.description,
      demo_access_code: demoCodeRaw ? demoCodeRaw.toUpperCase() : null,
      front_image: editingDepartment.value.frontImage,
      primary_color: editingDepartment.value.primaryColor,
      secondary_color: editingDepartment.value.secondaryColor,
    }).eq('id', editingDepartment.value.id)
    if (error) throw error
    await fetchDepartments()
    closeDepartmentModal()
    ElMessage.success('Department updated.')
  } catch (e) {
    console.error(e)
    ElMessage.error('Error updating department: ' + e.message)
  }
}

const deleteDepartment = async () => {
  if (!confirm('Delete this department? All its cards will also be deleted.')) return
  try {
    const { error: cardsErr } = await supabase.from('cards').delete().eq('departmentId', editingDepartment.value.id)
    if (cardsErr) throw cardsErr
    const { error: deptErr } = await supabase.from('departments').delete().eq('id', editingDepartment.value.id)
    if (deptErr) throw deptErr
    await fetchDepartments()
    await gameStore.fetchAllCards()
    closeDepartmentModal()
    ElMessage.success('Department deleted.')
  } catch (e) {
    console.error(e)
    ElMessage.error('Error deleting department: ' + e.message)
  }
}

const deleteDepartmentRow = async (dept) => {
  editingDepartment.value = {
    id: dept.id,
    name: dept.name,
    description: dept.description || '',
    demoAccessCode: dept.demo_access_code || '',
    frontImage: dept.front_image || '',
    primaryColor: dept.primary_color || '#000000',
    secondaryColor: dept.secondary_color || '#FFFFFF',
  }
  await deleteDepartment()
}

// ── Image upload ──────────────────────────────────────────────
const handleImageUpload = (type, event) => {
  const file = event.target.files[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = (e) => {
    if (type === 'front') editingDepartment.value.frontImage = e.target.result
  }
  reader.readAsDataURL(file)
}

const removeImage = (type) => {
  if (type === 'front') editingDepartment.value.frontImage = ''
  const input = document.getElementById(`card${type.charAt(0).toUpperCase() + type.slice(1)}Image`)
  if (input) input.value = ''
}

// ── Drag to reorder ───────────────────────────────────────────
const draggedDepartment = ref(null)
const draggedIndex = ref(null)
const departmentList = ref(null)

const startDrag = (event, dept, index) => {
  draggedDepartment.value = dept
  draggedIndex.value = index
  event.dataTransfer.effectAllowed = 'move'
  // Required by some browsers for drag/drop to activate reliably.
  event.dataTransfer.setData('text/plain', String(dept.id))
}

const resetDragState = () => {
  draggedDepartment.value = null
  draggedIndex.value = null
}

const onDrop = async (event, dropIndex) => {
  event.preventDefault()
  if (!draggedDepartment.value || draggedIndex.value === dropIndex) return
  try {
    const current = [...sortedDepartments.value]
    const [dragged] = current.splice(draggedIndex.value, 1)
    current.splice(dropIndex, 0, dragged)

    // Persist new ordering
    const updates = current.map((dept, index) =>
      supabase.from('departments').update({ position: index }).eq('id', dept.id)
    )
    const results = await Promise.all(updates)
    const failed = results.find(r => r.error)
    if (failed?.error) throw failed.error

    // Optimistic local update before refresh
    departments.value = current.map((dept, index) => ({ ...dept, position: index }))
    await fetchDepartments()
    ElMessage.success('Order updated.')
  } catch (e) {
    console.error(e)
    ElMessage.error('Error updating order: ' + e.message)
  } finally {
    resetDragState()
  }
}
</script>

<style lang="scss">
@import "@/styles/_variables";
@import "@/styles/_adminTabs";
@import "@/styles/_departments";

// Department-specific table additions
#manage-departments-page {
  .admin-table tbody tr {
    cursor: grab;
    &:active { cursor: grabbing; }
  }
}

tr.dragging { opacity: 0.35; }

.dept-name-cell {
  .dept-desc {
    display: block;
    font-size: var(--step--2);
    color: #6b7280;
    margin-top: 0.15rem;
    font-weight: 400;
  }
}

.drag-handle {
  color: #ccc;
  cursor: grab;
  width: 1.5rem;
  text-align: center;
  &:active { cursor: grabbing; }
  &:hover { color: #9ca3af; }
}

.text-muted { color: #aaa; }
</style>
